"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormDesigner = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const mobx_1 = require("mobx");
const mithril_component_1 = require("../../components/mithril.component");
const notification_service_1 = require("../../components/notification.toast/notification.service");
const spinner_1 = require("../../components/spinner/spinner");
const theme_1 = require("../../components/theme");
const i18n_1 = require("../../i18n");
const mithril_1 = require("../../mithril");
const page_1 = require("../../page/page");
const services_1 = require("../../services");
const store_1 = require("../../store");
const form_field_editor_1 = require("./editor/form.field.editor");
const form_field_editor_form_1 = require("./editor/form.field.editor.form");
const form_field_editor_height_1 = require("./editor/form.field.editor.height");
const form_field_editor_nested_list_1 = require("./editor/form.field.editor.nested.list");
const form_field_editor_select_list_1 = require("./editor/form.field.editor.select.list");
const form_field_editor_subform_1 = require("./editor/form.field.editor.subform");
const form_field_editor_vertical_separation_1 = require("./editor/form.field.editor.vertical.separation");
const form_designer_style_1 = require("./form.designer.style");
(0, theme_1.addStyles)(form_designer_style_1.Style);
class FormDesigner extends mithril_component_1.IMithrilStateComponent {
    constructor() {
        super();
        this.isEditing = false;
        this.isSaving = false;
        this.isNewForm = false;
        this.selectedFieldId = null;
        this.scrollToFieldId = null;
        this.fields = {};
        services_1.Services.Window.bindNavigateAway(i18n_1.T.DISCARD_CHANGES_CONFIRM, this.checkExit);
    }
    get nodes() {
        return (0, mithril_1.m)('div', { className: `${form_designer_style_1.CSS.container} ${this.isEditing ? form_designer_style_1.CSS.isEditing : form_designer_style_1.CSS.isReadOnly}` }, [
            this.viewHeader(),
            (0, mithril_1.m)('div', { className: form_designer_style_1.CSS.content }, [this.viewFields(), this.viewInfo()]),
        ]);
    }
    onRemove() {
        var _a;
        super.onRemove();
        services_1.Services.Window.unbindNavigateAway(this.checkExit);
        (_a = this.editState) === null || _a === void 0 ? void 0 : _a.destroy();
    }
    setForm(form) {
        var _a, _b, _c;
        this.form = (_a = store_1.AppStore.Form.edit(form)) === null || _a === void 0 ? void 0 : _a.form;
        // After saving a new form, clear the edit state.
        // Use the new form edit state despite the versions being the same.
        if (((_b = this.form) === null || _b === void 0 ? void 0 : _b.id) == null && form.id != null) {
            store_1.AppStore.Form.clearEdit(form);
            this.form = (_c = store_1.AppStore.Form.edit(form)) === null || _c === void 0 ? void 0 : _c.form;
        }
        this.reset();
    }
    setEditing(isEditing) {
        this.isEditing = isEditing;
    }
    get editState() {
        if (this.form && this.isEditing) {
            return store_1.AppStore.Form.edit(this.form);
        }
        return null;
    }
    viewHeader() {
        const headerButtons = [];
        if (this.isNewForm) {
            // If the user is internal then they are allowed to clear all fields.
            if (services_1.Services.User.isIkeAnalyst(services_1.Services.User.current)) {
                headerButtons.push((0, mithril_1.m)('button', {
                    className: 'mdl-button',
                    onclick: () => this.clearAllFields(),
                }, (0, i18n_1.Translate)(i18n_1.T.CLEAR_ALL_FIELDS)));
            }
            // Otherwise the will need to contact support
            else {
                headerButtons.push((0, mithril_1.m)('div', { className: form_designer_style_1.CSS.headerMsg }, [
                    (0, mithril_1.m)('span', [
                        `${(0, i18n_1.Translate)(i18n_1.T.CLEAR_FIELDS_HELP)} `,
                        (0, mithril_1.m)('a', {
                            target: '_blank',
                            href: 'https://support.ikegps.com/hc/en-us/requests/new?ticket_form_id=5786678993677',
                        }, (0, i18n_1.Translate)(i18n_1.T.HERE)),
                    ]),
                ]));
            }
            headerButtons.push((0, mithril_1.m)('button', {
                className: 'mdl-button',
                onclick: () => this.clearUnlockedFields(),
            }, (0, i18n_1.Translate)(i18n_1.T.CLEAR_UNLOCKED_FIELDS)));
        }
        headerButtons.push((0, mithril_1.m)('button', {
            className: 'mdl-button mdl-button--raised mdl-button--accent',
            disabled: !this.canSave,
            onclick: () => this.save(),
        }, this.isSaving ? spinner_1.Spinner.view() : (0, i18n_1.Translate)(i18n_1.T.SAVE)));
        return (0, mithril_1.m)('div', { className: form_designer_style_1.CSS.header }, [
            (0, mithril_1.m)('h6', (0, i18n_1.Translate)(i18n_1.T.FORM_EDIT_TITLE)),
            (0, mithril_1.m)('div', { style: 'display: flex; gap: 1.5em' }, headerButtons),
        ]);
    }
    clearUnlockedFields() {
        var _a, _b;
        for (const field of (_b = (_a = this.form) === null || _a === void 0 ? void 0 : _a.getFieldsRecursive) !== null && _b !== void 0 ? _b : []) {
            if (!field.isLocked) {
                // Clean up the editor state before deleting the field
                const editor = this.fields[field.fieldId];
                if (editor) {
                    editor.state.destroy();
                    delete this.fields[field.fieldId];
                }
                field.Mutator.delete();
            }
        }
        // Clear selection if the selected field was deleted
        if (this.form) {
            this.selectedFieldId = this.form.fieldId;
        }
    }
    clearAllFields() {
        if (this.form) {
            for (const field of this.form.getFieldsRecursive) {
                // Clean up the editor state before deleting the field
                const editor = this.fields[field.fieldId];
                if (editor) {
                    editor.state.destroy();
                    delete this.fields[field.fieldId];
                }
                field.Mutator.delete();
            }
            this.form.Mutator.clearType();
            this.form.Mutator.displayName('');
            this.form.Mutator.description('');
            // Reset selection to the form since all fields were deleted
            this.selectedFieldId = this.form.fieldId;
        }
    }
    viewFields() {
        if (!this.form) {
            return null;
        }
        if (this.selectedFieldId == null) {
            this.selectedFieldId = this.form.fieldId;
        }
        return (0, mithril_1.m)('div', { className: form_designer_style_1.CSS.field.container }, this.viewField(this.form));
    }
    viewInfo() {
        var _a;
        if (this.selectedFieldId == null || this.fields[this.selectedFieldId] == null) {
            return (0, mithril_1.m)('div', { className: form_designer_style_1.CSS.info.container });
        }
        return (0, mithril_1.m)('div', { className: form_designer_style_1.CSS.info.container }, (_a = this.fields[this.selectedFieldId]) === null || _a === void 0 ? void 0 : _a.viewInfo());
    }
    createEditor(f) {
        if (f === this.form) {
            return new form_field_editor_form_1.FormFieldEditorForm(this, f);
        }
        if (core_shared_1.FormModelFieldSubForm.isSubForm(f)) {
            return new form_field_editor_subform_1.FormFieldEditorSubForm(this, f);
        }
        if (core_shared_1.FormModelFieldNestedList.isNestedList(f)) {
            return new form_field_editor_nested_list_1.FormFieldEditorNestedList(this, f);
        }
        if (core_shared_1.FormModelFieldSelectList.isSelectList(f)) {
            return new form_field_editor_select_list_1.FormFieldEditorSelectList(this, f);
        }
        if (f.type === core_api_1.IkeFieldType.HEIGHT) {
            return new form_field_editor_height_1.FormFieldEditorHeight(this, f);
        }
        if (f.type === core_api_1.IkeFieldType.VERTICAL_SEPARATION) {
            return new form_field_editor_vertical_separation_1.FormFieldEditorVerticalSeparation(this, f);
        }
        return new form_field_editor_1.FormFieldEditor(this, f);
    }
    createField(form, type) {
        const field = form.Mutator.create(type, core_shared_1.IkeId.generate());
        this.setSelected(field);
        this.setScrollToField(field);
    }
    setScrollToField(field) {
        if (field == null) {
            this.scrollToFieldId = null;
        }
        else {
            this.scrollToFieldId = field.fieldId;
        }
    }
    reset() {
        var _a;
        if (this.isEditing) {
            (_a = this.editState) === null || _a === void 0 ? void 0 : _a.reset();
        }
        for (const fieldId of Object.keys(this.fields)) {
            const fieldState = this.fields[fieldId];
            fieldState === null || fieldState === void 0 ? void 0 : fieldState.state.destroy();
        }
        this.fields = {};
        if (this.form) {
            this.setSelected(this.form);
        }
    }
    get canSave() {
        if (!this.editState || !this.isEditing) {
            return false;
        }
        return (this.isNewForm || this.editState.isDirty) && !this.isSaving && this.editState.canSubmit;
    }
    checkExit() {
        var _a, _b;
        return (_b = (_a = this.editState) === null || _a === void 0 ? void 0 : _a.isDirty) !== null && _b !== void 0 ? _b : false;
    }
    getEditor(f) {
        var _a;
        let editor = this.fields[f.fieldId];
        if (editor == null) {
            editor = this.fields[f.fieldId] = this.createEditor(f);
            (_a = this.editState) === null || _a === void 0 ? void 0 : _a.inputStates.add(editor.state);
        }
        return editor;
    }
    viewField(f) {
        const nodes = this.getEditor(f).viewTree();
        if (this.scrollToFieldId === f.fieldId && nodes) {
            nodes.attrs.oncreate = (ctx) => {
                ctx.dom.scrollIntoView();
                this.setScrollToField(undefined);
            };
        }
        return nodes;
    }
    setSelected(f) {
        if (f == null) {
            this.selectedFieldId = null;
        }
        else {
            this.selectedFieldId = f.fieldId;
        }
    }
    save() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.editState == null || !this.canSave) {
                return;
            }
            this.setSaving(true);
            let selectedFieldId;
            if (this.selectedFieldId != null) {
                selectedFieldId = this.selectedFieldId.split(':').pop();
            }
            try {
                const wasNewForm = this.isNewForm;
                const newForm = yield services_1.Services.Form.save(this.editState.form);
                this.setForm(newForm);
                if (wasNewForm) {
                    this.isNewForm = false;
                    services_1.Services.Route.go(page_1.IkePageName.formEdit, { version: newForm.version });
                }
                else {
                    services_1.Services.Route.setParam('version', newForm.version);
                    if (selectedFieldId != null) {
                        const selectedField = newForm.findField(selectedFieldId);
                        if (selectedField) {
                            this.setSelected(selectedField);
                        }
                    }
                }
            }
            catch (e) {
                notification_service_1.NotificationService.error({ key: i18n_1.T.FORM_ERROR_SAVING });
                services_1.Services.Log.Logger.error(e);
            }
            this.setSaving(false);
        });
    }
    setSaving(isSaving) {
        this.isSaving = isSaving;
    }
}
exports.FormDesigner = FormDesigner;
tslib_1.__decorate([
    mobx_1.observable
], FormDesigner.prototype, "isEditing", void 0);
tslib_1.__decorate([
    mobx_1.observable
], FormDesigner.prototype, "isSaving", void 0);
tslib_1.__decorate([
    mobx_1.observable
], FormDesigner.prototype, "isNewForm", void 0);
tslib_1.__decorate([
    mobx_1.observable.ref
], FormDesigner.prototype, "form", void 0);
tslib_1.__decorate([
    mobx_1.observable
], FormDesigner.prototype, "selectedFieldId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], FormDesigner.prototype, "scrollToFieldId", void 0);
tslib_1.__decorate([
    mobx_1.action
], FormDesigner.prototype, "setForm", null);
tslib_1.__decorate([
    mobx_1.action
], FormDesigner.prototype, "setEditing", null);
tslib_1.__decorate([
    mobx_1.action
], FormDesigner.prototype, "clearUnlockedFields", null);
tslib_1.__decorate([
    mobx_1.action
], FormDesigner.prototype, "clearAllFields", null);
tslib_1.__decorate([
    mobx_1.action
], FormDesigner.prototype, "createField", null);
tslib_1.__decorate([
    mobx_1.action
], FormDesigner.prototype, "setScrollToField", null);
tslib_1.__decorate([
    mobx_1.action
], FormDesigner.prototype, "reset", null);
tslib_1.__decorate([
    mobx_1.action.bound
], FormDesigner.prototype, "checkExit", null);
tslib_1.__decorate([
    mobx_1.action
], FormDesigner.prototype, "setSelected", null);
tslib_1.__decorate([
    mobx_1.action
], FormDesigner.prototype, "setSaving", null);
//# sourceMappingURL=form.designer.js.map